import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
    TextField,
    FormControl,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    IconButton,
} from '@material-ui/core';
import { useParams, useRouteMatch } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { savePage } from '../../actions/pageActions';
import { ocrTypes, navPath, navSearch } from '../../constants';
import { HV_THEME, HVHeader, HVButton, Card, Icons, StatusPill } from '../shared/DesignSystem';

function BenchmarkList(props) {
    const { projectId, groupId } = useParams();
    const match = useRouteMatch();
    const benchmarkCategory = match.path.includes('project') ? 'project' : 'group';
    const id = projectId || groupId;

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [benchmarks, setBenchmarks] = useState([]);
    const [name, setName] = useState('');
    const [ocrType, setOCRType] = useState('');
    const [benchmarkFormat, setBenchmarkFormat] = useState('');
    const [openCreate, setOpenCreate] = useState(false);
    const [openClone, setOpenClone] = useState(false);
    const [cloneName, setCloneName] = useState('');
    const [cloneId, setCloneId] = useState(id);
    const [cloneOCRType, setCloneOCRType] = useState('');
    const [benchmarkId, setBenchmarkId] = useState('');
    const [headerName, setHeaderName] = useState(props.location.state ? props.location.state.name : '');
    const [docType, setDocType] = useState(props.location.state ? props.location.state.docType : '');

    const getBenchmarks = () => {
        axios
            .get('/api/benchmarks/list', {
                params: { benchmarkType: benchmarkCategory, id }
            })
            .then((data) => {
                if (data.data[0] && data.data[0].benchmarks)
                    setBenchmarks(data.data[0].benchmarks);
            })
            .catch((e) => console.log(e));
    };

    const getDetails = () => {
        if (benchmarkCategory === 'project') {
            axios.post('/api/projects/status', { projectId: id })
                .then(res => { setHeaderName(res.data.name); setDocType(res.data.type); });
        } else {
            axios.post('/api/groups/status', { groupId: id })
                .then(res => { setHeaderName(res.data.name); setDocType(res.data.type); });
        }
    };

    useEffect(() => {
        getBenchmarks();
        if (!headerName) getDetails();
        dispatch(savePage(0, 10));
    }, [id]);

    const handleCreate = () => {
        let format = benchmarkFormat;
        if (benchmarkCategory === 'group') format = 'newData';
        const data = {
            benchmark: { name, ocrType, benchmarkType: benchmarkCategory, benchmarkFormat: format, id, status: 'created' },
        };
        axios.post('/api/benchmarks/create', data)
            .then(() => {
                enqueueSnackbar('Benchmark created successfully', { variant: 'success' });
                setName(''); setOCRType(''); setBenchmarkFormat('');
                setOpenCreate(false);
                getBenchmarks();
            })
            .catch(() => enqueueSnackbar('Error while creating benchmark', { variant: 'error' }));
    };

    const handleClick = (bName, benchmark, bId, benchmarkType, bOcrType, status) => {
        props.history.push({
            pathname: `/benchmark/${benchmark}`,
            search: navSearch(),
            state: { name: bName, benchmark, id: bId, benchmarkType, ocrType: bOcrType, status, docType },
        });
    };

    const handleClone = (bId) => {
        const benchmark = benchmarks.find((b) => b._id === bId);
        setCloneName(benchmark.name + ' Clone');
        setCloneOCRType(benchmark.ocrType);
        setOpenClone(true);
        setBenchmarkId(bId);
    };

    const handleCloseClone = () => {
        setOpenClone(false);
        setBenchmarkId('');
    };

    const handleCreateClone = () => {
        const data = { benchmarkId, name: cloneName, projectId: cloneId, ocrType: cloneOCRType };
        axios.post('/api/benchmarks/clone', data)
            .then(() => {
                enqueueSnackbar('Benchmark cloned successfully', { variant: 'success' });
                setOpenClone(false); setBenchmarkId(''); setCloneName(''); setCloneId(id);
                getBenchmarks();
            })
            .catch(() => enqueueSnackbar('Error while cloning benchmark', { variant: 'error' }));
    };

    const handleGoBack = () => {
        if (benchmarkCategory === 'project') {
            props.history.push(navPath(`/project/${id}`));
        } else {
            props.history.push(navPath(`/group/${id}`));
        }
    };

    const categoryLabel = benchmarkCategory === 'project' ? 'Projects' : 'Groups';
    const tabId = benchmarkCategory === 'project' ? 'projects' : 'groups';

    const STATUS_TONE = {
        created: { tone: 'neutral', label: 'Created' },
        running: { tone: 'info', label: 'Running' },
        completed: { tone: 'ok', label: 'Completed' },
        failed: { tone: 'danger', label: 'Failed' },
    };

    return (
        <div style={{ background: HV_THEME.bg, minHeight: '100vh', paddingBottom: 60, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: HV_THEME.ink }}>
            <HVHeader
                tab={tabId}
                showTabs={false}
                role={{ name: 'Admin', initials: 'AD', color: '#e6194b', label: 'Admin' }}
                env="STAGING"
                onLogout={() => props.history.push('/login')}
            />

            {/* Breadcrumb bar */}
            <div style={{ background: '#fff', borderBottom: '1px solid #ececec', padding: '14px 40px' }}>
                <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <HVButton variant="ghost" size="sm" icon={<Icons.arrowL size={14} />} onClick={handleGoBack}>Back</HVButton>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: HV_THEME.mute }}>
                        <span style={{ cursor: 'pointer' }} onClick={() => props.history.push('/')}>{categoryLabel}</span>
                        <span>/</span>
                        <span style={{ cursor: 'pointer' }} onClick={handleGoBack}>{headerName || id}</span>
                        <span>/</span>
                        <span style={{ color: HV_THEME.ink, fontWeight: 600 }}>Benchmarks</span>
                    </div>
                </div>
            </div>

            <div style={{ padding: '28px 40px', maxWidth: 1440, margin: '0 auto' }}>
                {/* Page header */}
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 28 }}>
                    <div>
                        <div style={{ fontSize: 10, letterSpacing: '0.18em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase' }}>{categoryLabel} / {headerName || id}</div>
                        <h1 style={{ fontSize: 28, fontWeight: 700, margin: '6px 0 4px', letterSpacing: '-0.02em' }}>Benchmarks</h1>
                        <div style={{ fontSize: 13, color: HV_THEME.mute }}>{benchmarks.length} total</div>
                    </div>
                    <HVButton variant="primary" icon={<Icons.plus size={14} />} onClick={() => setOpenCreate(true)}>
                        New benchmark
                    </HVButton>
                </div>

                {/* Benchmark cards grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                    {benchmarks.length === 0 ? (
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Card pad={0}>
                                <div style={{ padding: '60px 20px', textAlign: 'center', color: HV_THEME.mute }}>
                                    <div style={{ width: 56, height: 56, borderRadius: 99, background: '#f4f4f4', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                                        <Icons.beaker size={24} />
                                    </div>
                                    <div style={{ fontSize: 16, fontWeight: 600, color: HV_THEME.ink, marginBottom: 4 }}>No benchmarks yet</div>
                                    <div style={{ fontSize: 13 }}>Create your first benchmark to get started.</div>
                                </div>
                            </Card>
                        </div>
                    ) : benchmarks.map((benchmark) => {
                        const tone = STATUS_TONE[benchmark.status] || { tone: 'neutral', label: benchmark.status || 'Created' };
                        const isMismatch = benchmark.id !== id;
                        return (
                            <Card key={benchmark._id} pad={0} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                <div style={{ padding: 16, paddingBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                                        <div style={{ width: 40, height: 40, borderRadius: 4, background: '#eef2ff', color: '#3949ab', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                                            <Icons.beaker size={18} />
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            {isMismatch && <span title="Benchmark ID mismatch" style={{ fontSize: 12 }}>🚩</span>}
                                            <StatusPill tone={tone.tone} size="sm">{tone.label}</StatusPill>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={benchmark.name}>{benchmark.name}</div>
                                    <div style={{ fontSize: 11, color: HV_THEME.mute, fontFamily: "'JetBrains Mono', monospace", marginBottom: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
                                        onClick={() => { navigator.clipboard.writeText(benchmark._id); enqueueSnackbar(`${benchmark._id} copied`, { variant: 'success', autoHideDuration: 800 }); }}>
                                        {benchmark._id} <Icons.copy size={10} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                                        <div>
                                            <div style={{ fontSize: 9, letterSpacing: '0.12em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase', marginBottom: 3 }}>OCR Type</div>
                                            <div style={{ fontSize: 12, fontWeight: 600 }}>{benchmark.ocrType || '—'}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 9, letterSpacing: '0.12em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase', marginBottom: 3 }}>Format</div>
                                            <div style={{ fontSize: 12, fontWeight: 600 }}>{benchmark.benchmarkFormat || benchmark.benchmarkType || '—'}</div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ borderTop: '1px solid #f0f0f0', padding: '10px 12px', display: 'flex', gap: 6, alignItems: 'center', background: '#fafafa', marginTop: 'auto' }}>
                                    <HVButton size="sm" variant="secondary" style={{ flex: 1 }}
                                        onClick={() => handleClick(benchmark.name, benchmark._id, benchmark.id, benchmark.benchmarkType, benchmark.ocrType, benchmark.status)}>
                                        Open
                                    </HVButton>
                                    <HVButton size="sm" variant="ghost" icon={<Icons.copy size={12} />} onClick={() => handleClone(benchmark._id)}>
                                        Clone
                                    </HVButton>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </div>

            {/* Create benchmark dialog */}
            <Dialog open={openCreate} onClose={() => setOpenCreate(false)} aria-labelledby="create-benchmark" maxWidth="sm" fullWidth>
                <DialogTitle id="create-benchmark" style={{ position: 'relative', paddingRight: 48 }}>
                    New Benchmark
                    <IconButton onClick={() => setOpenCreate(false)} style={{ position: 'absolute', right: 8, top: 8 }}>
                        <Icons.x size={18} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        required autoFocus margin="normal" id="name" label="Benchmark Name"
                        type="text" fullWidth variant="outlined" onChange={e => setName(e.target.value)} value={name}
                    />
                    <FormControl variant="outlined" style={{ marginTop: 8, width: '100%' }}>
                        <TextField select label="OCR Type" id="ocr" value={ocrType} onChange={e => setOCRType(e.target.value)} variant="outlined" fullWidth>
                            {Object.keys(ocrTypes).map((t) => <MenuItem key={t} value={t}>{ocrTypes[t]}</MenuItem>)}
                        </TextField>
                    </FormControl>
                    {benchmarkCategory === 'project' && (
                        <FormControl variant="outlined" style={{ marginTop: 14, width: '100%' }}>
                            <TextField select label="Benchmark Type" id="benchmarkType" value={benchmarkFormat} onChange={e => setBenchmarkFormat(e.target.value)} variant="outlined" fullWidth>
                                <MenuItem value="testSplit">Test Split</MenuItem>
                                <MenuItem value="newData">New Data</MenuItem>
                            </TextField>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
                    <HVButton variant="ghost" onClick={() => setOpenCreate(false)}>Cancel</HVButton>
                    <HVButton variant="primary" onClick={handleCreate}>Create</HVButton>
                </DialogActions>
            </Dialog>

            {/* Clone dialog */}
            <Dialog open={openClone} onClose={handleCloseClone} aria-labelledby="clone-benchmark" maxWidth="sm" fullWidth>
                <DialogTitle id="clone-benchmark" style={{ position: 'relative', paddingRight: 48 }}>
                    Clone Benchmark
                    <IconButton onClick={handleCloseClone} style={{ position: 'absolute', right: 8, top: 8 }}>
                        <Icons.x size={18} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        required margin="normal" id="clone-name" label="Benchmark Name"
                        type="text" fullWidth variant="outlined" onChange={e => setCloneName(e.target.value)} value={cloneName}
                    />
                    <TextField
                        required margin="normal" id="clone-project" label="Project ID"
                        type="text" fullWidth variant="outlined" onChange={e => setCloneId(e.target.value)} value={cloneId}
                    />
                    <FormControl variant="outlined" style={{ marginTop: 8, width: '100%' }}>
                        <TextField select label="OCR Type" value={cloneOCRType} onChange={e => setCloneOCRType(e.target.value)} variant="outlined" fullWidth>
                            {Object.keys(ocrTypes).map((t) => <MenuItem key={t} value={t}>{ocrTypes[t]}</MenuItem>)}
                        </TextField>
                    </FormControl>
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
                    <HVButton variant="ghost" onClick={handleCloseClone}>Cancel</HVButton>
                    <HVButton variant="primary" onClick={handleCreateClone}>Clone</HVButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default BenchmarkList;
